import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { BasePalette } from 'styles/theme';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { formatCurrencyOrNumber } from 'utils/shared/number/roundToDecimals';
import {
  BucketData,
  BucketSizeValue,
  FlowMetric,
  METRIC_OPTIONS,
  BUCKET_SIZE_OPTIONS,
  METRIC_Y_LABELS,
  isSignedFlowMetric,
  isSameBucketValue,
} from 'hooks/useBucketedFlowData';

interface Props {
  data: BucketData[];
  metric: FlowMetric;
  bucketSize: BucketSizeValue;
  ticker?: string;
  onMetricChange: (metric: FlowMetric) => void;
  onBucketSizeChange: (size: BucketSizeValue) => void;
  mode: 'total' | 'net';
  onModeChange: (mode: 'total' | 'net') => void;
  bucketOptions?: { value: BucketSizeValue; label: string }[];
  defaultShowReferenceLine?: boolean;
  chartMinHeight?: number;
  referenceLabel?: string;
  priceButtonLabel?: string;
}

const REFERENCE_LINE_STORAGE_KEY = 'flowGraph.showReferencePrice';

const COLORS = {
  call: BasePalette.GraphGreen,
  put: BasePalette.GraphRed,
  price: BasePalette.SkyBlue,
} as const;

const BTN_BASE = 'rounded px-2 py-1 text-xs transition-colors';
const BTN_ACTIVE = 'bg-button-color text-white hover:bg-button-hover';
const BTN_IDLE = 'bg-gray-800 text-gray-400 hover:bg-gray-700';
const btnClass = (active: boolean) => `${BTN_BASE} ${active ? BTN_ACTIVE : BTN_IDLE}`;

const TOOLTIP_CURSOR = { stroke: '#6b7280', strokeDasharray: '3 3' } as const;
const X_TICK = { fontSize: 11, fill: '#9ca3af' };
const X_AXIS_LINE = { stroke: '#374151' };
const Y_RIGHT_TICK = { fontSize: 11, fill: COLORS.price };
const Y_RIGHT_AXIS_LINE = { stroke: COLORS.price, strokeOpacity: 0.3 };
const CHART_MARGIN = { top: 10, right: 4, left: 0, bottom: 8 };
const AXIS_TITLE_FILL = '#9ca3af';
const AXIS_TITLE_FONT_SIZE = 11;
// Target number of x-axis labels to render at any range (e.g. 30 days of
// hourly data still ends up with ~this many ticks). Tooltip carries full
// per-bucket detail on hover.
const X_AXIS_LABEL_TARGET = 8;
const TNUM_STYLE: React.CSSProperties = { fontFeatureSettings: '"tnum"' };

function formatPrice(value: number): string {
  return `$${formatCurrencyOrNumber(value, { decimals: 0 })}`;
}

function formatYTick(value: number, metric: FlowMetric): string {
  const info = METRIC_Y_LABELS[metric];
  if (metric === 'avgIV') return `${value.toFixed(1)}%`;
  const sign = value < 0 ? '-' : '';
  return `${sign}${info.prefix}${abbreviateNumber(Math.abs(value))}`;
}

function formatMetricValue(value: number, metric: FlowMetric, showSign = false): string {
  const info = METRIC_Y_LABELS[metric];
  if (metric === 'avgIV') return `${value.toFixed(2)}%`;
  const sign = showSign && value < 0 ? '-' : '';
  return `${sign}${info.prefix}${abbreviateNumber(Math.abs(value))}`;
}

// Show full price with thousands separator. Abbreviating ($6.7K) collapses
// all ticks for high-priced indices like SPX where the intraday range is
// ~1% of the base — labels become indistinguishable.
const formatPriceTick = (v: number) =>
  `$${formatCurrencyOrNumber(v, { decimals: v < 100 ? 2 : 0 })}`;

interface FlowTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ payload?: BucketData }>;
  metric: FlowMetric;
  mode: 'total' | 'net';
}

const FlowTooltip = memo(function FlowTooltip({
  active,
  payload,
  label,
  metric,
  mode,
}: FlowTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  const netPositive = data.netValue >= 0;
  const showSignedSplitValues = isSignedFlowMetric(metric);

  return (
    <div
      className="rounded-md border border-gray-700 bg-gray-900/95 px-3 py-2 text-xs shadow-lg"
      style={TNUM_STYLE}
    >
      <p className="mb-1 font-semibold text-gray-300">{label}</p>
      {mode === 'total' ? (
        <>
          <p style={{ color: COLORS.call }}>
            Calls: {formatMetricValue(data.callValue, metric, showSignedSplitValues)}
          </p>
          <p style={{ color: COLORS.put }}>
            Puts: {formatMetricValue(data.putValue, metric, showSignedSplitValues)}
          </p>
        </>
      ) : (
        <p style={{ color: netPositive ? COLORS.call : COLORS.put }}>
          Net: {netPositive ? '+' : '-'}
          {formatMetricValue(Math.abs(data.netValue), metric)}
        </p>
      )}
      {data.price > 0 && <p style={{ color: COLORS.price }}>Price: {formatPrice(data.price)}</p>}
      <p className="mt-1 text-gray-500">{data.tradeCount} trades</p>
    </div>
  );
});

function FlowGraph({
  data,
  metric,
  bucketSize,
  ticker,
  onMetricChange,
  onBucketSizeChange,
  mode,
  onModeChange,
  bucketOptions,
  defaultShowReferenceLine = true,
  chartMinHeight = 300,
  referenceLabel = 'Reference Price',
  priceButtonLabel = 'Price',
}: Props) {
  const hasData = data.length > 0;
  const isAvgIVMetric = metric === 'avgIV';
  const effectiveMode = isAvgIVMetric ? 'total' : mode;

  const [showReferenceLine, setShowReferenceLine] = useState(defaultShowReferenceLine);

  // Hydrate from localStorage post-mount to avoid SSR mismatch on the table.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(REFERENCE_LINE_STORAGE_KEY);
      if (stored !== null) setShowReferenceLine(stored === 'true');
    } catch {
      // localStorage may be unavailable (private mode, SSR) — fall back to default.
    }
  }, []);

  const handleToggleReferenceLine = useCallback(() => {
    setShowReferenceLine((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(REFERENCE_LINE_STORAGE_KEY, String(next));
      } catch {
        // ignore — preference is best-effort
      }
      return next;
    });
  }, []);

  const handleMetricChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onMetricChange(e.target.value as FlowMetric),
    [onMetricChange],
  );

  const handleModeTotal = useCallback(() => onModeChange('total'), [onModeChange]);
  const handleModeNet = useCallback(() => onModeChange('net'), [onModeChange]);

  const yTickFormatter = useCallback((v: number) => formatYTick(v, metric), [metric]);

  const tooltipContent = useCallback(
    (props: any) => <FlowTooltip {...props} metric={metric} mode={effectiveMode} />,
    [effectiveMode, metric],
  );

  const resolvedBucketOptions = bucketOptions ?? BUCKET_SIZE_OPTIONS;

  const bucketButtons = useMemo(
    () =>
      resolvedBucketOptions.map((opt) => (
        <button
          key={opt.label}
          onClick={() => onBucketSizeChange(opt.value)}
          className={btnClass(isSameBucketValue(bucketSize, opt.value))}
          style={TNUM_STYLE}
        >
          {opt.label}
        </button>
      )),
    [bucketSize, onBucketSizeChange, resolvedBucketOptions],
  );

  const chartTitle = isAvgIVMetric
    ? `${ticker || 'Market'} ${METRIC_Y_LABELS[metric].label} Flow`
    : `${ticker || 'Market'} ${effectiveMode === 'net' ? 'Net' : 'Total'} ${
        METRIC_Y_LABELS[metric].label
      } Flow`;

  const modeToggle = (
    <div className="flex items-center gap-1" role="group" aria-label="Flow display mode">
      <button
        type="button"
        onClick={handleModeTotal}
        aria-pressed={mode === 'total'}
        className={btnClass(mode === 'total')}
      >
        Total
      </button>
      <button
        type="button"
        onClick={handleModeNet}
        aria-pressed={mode === 'net'}
        className={btnClass(mode === 'net')}
      >
        Net
      </button>
    </div>
  );

  const netGradientOffset = useMemo(() => {
    if (data.length === 0) return 1;
    const max = Math.max(...data.map((d) => d.netValue));
    const min = Math.min(...data.map((d) => d.netValue));
    if (max <= 0) return 0;
    if (min >= 0) return 1;
    return max / (max - min);
  }, [data]);

  const isMultiDayBucket = typeof bucketSize !== 'number' && bucketSize.unit !== 'm';

  const xTickInterval: number | 'preserveStartEnd' = useMemo(() => {
    if (!isMultiDayBucket) return 'preserveStartEnd';
    if (data.length <= X_AXIS_LABEL_TARGET) return 0;
    return Math.max(0, Math.ceil(data.length / X_AXIS_LABEL_TARGET) - 1);
  }, [isMultiDayBucket, data.length]);

  return (
    <div className="flex h-full w-full flex-col p-2" style={TNUM_STYLE}>
      {/* Controls row */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select
          value={metric}
          onChange={handleMetricChange}
          className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-gray-200"
        >
          {METRIC_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="flex flex-wrap items-center gap-1">{bucketButtons}</div>

        {!isAvgIVMetric && (
          <>
            <span className="hidden h-4 border-l border-gray-700 sm:block" aria-hidden="true" />
            {modeToggle}
          </>
        )}

        <button
          type="button"
          onClick={handleToggleReferenceLine}
          aria-pressed={showReferenceLine}
          title={`${showReferenceLine ? 'Hide' : 'Show'} ${referenceLabel}`}
          className={`${btnClass(showReferenceLine)} ml-auto`}
          style={TNUM_STYLE}
        >
          {priceButtonLabel}
        </button>
      </div>

      <div className="mb-1 text-center text-sm font-semibold text-white sm:text-base">
        {chartTitle}
      </div>

      {!hasData ? (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
          No flow data available for the selected filters.
        </div>
      ) : (
        <div className="w-full flex-1" style={{ minHeight: chartMinHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={CHART_MARGIN}>
              <defs>
                <linearGradient id="flowCallGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.call} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={COLORS.call} stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="flowPutGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.put} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={COLORS.put} stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id="flowNetFillGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={netGradientOffset} stopColor={COLORS.call} stopOpacity={0.4} />
                  <stop offset={netGradientOffset} stopColor={COLORS.put} stopOpacity={0.4} />
                </linearGradient>
                <linearGradient id="flowNetLineGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={netGradientOffset} stopColor={COLORS.call} />
                  <stop offset={netGradientOffset} stopColor={COLORS.put} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#272727" vertical={false} />

              <XAxis
                dataKey="displayLabel"
                tick={isMultiDayBucket ? { fontSize: 9, fill: '#9ca3af' } : X_TICK}
                tickLine={false}
                axisLine={X_AXIS_LINE}
                interval={xTickInterval}
                minTickGap={isMultiDayBucket ? 60 : 24}
                angle={isMultiDayBucket ? -30 : 0}
                textAnchor={isMultiDayBucket ? 'end' : 'middle'}
                height={isMultiDayBucket ? 80 : 56}
                tickMargin={8}
              />

              <YAxis
                yAxisId="left"
                tick={X_TICK}
                tickFormatter={yTickFormatter}
                tickLine={false}
                axisLine={X_AXIS_LINE}
                width={88}
                tickMargin={8}
                tickCount={10}
                label={{
                  value: METRIC_Y_LABELS[metric].label,
                  angle: -90,
                  position: 'insideLeft',
                  offset: 10,
                  style: {
                    textAnchor: 'middle',
                    fill: AXIS_TITLE_FILL,
                    fontSize: AXIS_TITLE_FONT_SIZE,
                  },
                }}
              />

              {/* Right axis stays mounted regardless of the toggle so the
                    plot area width never changes — toggling visibility only
                    affects what's painted, not the chart geometry, which
                    avoids the bars/areas re-interpolating on toggle. */}
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={showReferenceLine ? Y_RIGHT_TICK : false}
                tickFormatter={formatPriceTick}
                tickLine={false}
                axisLine={showReferenceLine ? Y_RIGHT_AXIS_LINE : false}
                domain={['auto', 'auto']}
                width={84}
                tickMargin={8}
                tickCount={10}
                label={
                  showReferenceLine
                    ? {
                        value: referenceLabel,
                        angle: 90,
                        position: 'insideRight',
                        offset: 15,
                        style: {
                          textAnchor: 'middle',
                          fill: COLORS.price,
                          fontSize: AXIS_TITLE_FONT_SIZE,
                        },
                      }
                    : undefined
                }
              />

              <Tooltip content={tooltipContent} cursor={TOOLTIP_CURSOR} />

              <ReferenceLine yAxisId="left" y={0} stroke="#4b5563" strokeWidth={1} />

              {effectiveMode === 'total' ? (
                <>
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="callValue"
                    stroke={COLORS.call}
                    strokeWidth={1.5}
                    fill="url(#flowCallGrad)"
                    fillOpacity={0.7}
                    name="Calls"
                    isAnimationActive={false}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="putValue"
                    stroke={COLORS.put}
                    strokeWidth={1.5}
                    fill="url(#flowPutGrad)"
                    fillOpacity={0.7}
                    name="Puts"
                    isAnimationActive={false}
                  />
                </>
              ) : (
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="netValue"
                  stroke="url(#flowNetLineGrad)"
                  strokeWidth={2}
                  fill="url(#flowNetFillGrad)"
                  name="Net Flow"
                  isAnimationActive={false}
                />
              )}

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="price"
                stroke={COLORS.price}
                strokeWidth={1}
                dot={false}
                name={referenceLabel}
                isAnimationActive={false}
                hide={!showReferenceLine}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default memo(FlowGraph);
